import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { prebuilt, type PrebuiltCampaign } from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  ArrowLeft, Search, Mail, Calendar, Clock, Target,
  Download, Zap, Filter, Star, ChevronDown,
  ShoppingCart, Monitor, Home, Wrench, GraduationCap, Building2,
  HeartPulse, Globe, Sparkles
} from "lucide-react";

// ── Industry config ──
const INDUSTRIES = [
  { value: "",              label: "All Industries", icon: Globe,         color: "bg-slate/10 text-slate" },
  { value: "ecommerce",     label: "E-commerce",     icon: ShoppingCart,  color: "bg-coral-light text-coral" },
  { value: "saas",          label: "SaaS",           icon: Monitor,       color: "bg-blue-50 text-blue-600" },
  { value: "real_estate",   label: "Real Estate",    icon: Home,          color: "bg-emerald-50 text-emerald-600" },
  { value: "local_services",label: "Local Services", icon: Wrench,        color: "bg-amber-50 text-amber-700" },
  { value: "education",     label: "Education",      icon: GraduationCap, color: "bg-purple-50 text-purple-600" },
  { value: "agency",        label: "Agency / B2B",   icon: Building2,     color: "bg-indigo-50 text-indigo-600" },
  { value: "health",        label: "Health",         icon: HeartPulse,    color: "bg-rose-50 text-rose-600" },
  { value: "general",       label: "Universal",      icon: Sparkles,      color: "bg-teal-light text-teal" },
];

const DIFFICULTY_BADGE: Record<string, string> = {
  beginner: "bg-emerald-50 text-emerald-700 border-emerald-200",
  intermediate: "bg-amber-50 text-amber-700 border-amber-200",
  advanced: "bg-rose-50 text-rose-700 border-rose-200",
};

function industryConfig(industry: string) {
  return INDUSTRIES.find(i => i.value === industry) || INDUSTRIES[0];
}

export default function PrebuiltLibraryPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<PrebuiltCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [industry, setIndustry] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await prebuilt.list({
        industry: industry || undefined,
        search: search || undefined,
      });
      setCampaigns(res.campaigns);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [industry, search]);

  useEffect(() => { load(); }, [load]);

  // Debounced search
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filtered = campaigns;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <button onClick={() => navigate("/campaigns")} className="p-2 rounded-lg hover:bg-black/[0.04] transition-colors">
          <ArrowLeft size={18} className="text-slate" />
        </button>
        <div>
          <h1 className="text-2xl font-display font-bold text-ink">Campaign Library</h1>
          <p className="text-slate text-sm mt-0.5">Pre-built email sequences — pick one, customize, and send.</p>
        </div>
      </div>

      {/* Search + Filter bar */}
      <div className="mt-6 mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate/50" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search campaigns..."
            className="w-full pl-9 pr-4 py-2.5 rounded-lg border border-sand bg-white text-sm font-body focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral transition-all"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${showFilters ? "bg-coral text-white border-coral" : "border-sand bg-white text-slate hover:border-coral/40"}`}
        >
          <Filter size={14} /> Filter {industry && "• 1"}
          <ChevronDown size={14} className={`transition-transform ${showFilters ? "rotate-180" : ""}`} />
        </button>
      </div>

      {/* Industry pills */}
      {showFilters && (
        <div className="mb-6 flex flex-wrap gap-2 pb-4 border-b border-sand">
          {INDUSTRIES.map((ind) => {
            const Icon = ind.icon;
            const active = industry === ind.value;
            return (
              <button
                key={ind.value}
                onClick={() => setIndustry(ind.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  active
                    ? "bg-coral text-white border-coral shadow-sm"
                    : "bg-white border-sand text-slate hover:border-coral/40 hover:text-ink"
                }`}
              >
                <Icon size={13} /> {ind.label}
              </button>
            );
          })}
        </div>
      )}

      {/* Results count */}
      {!loading && (
        <p className="text-xs text-slate mb-4">
          {filtered.length} campaign{filtered.length !== 1 ? "s" : ""} available
        </p>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-20">
          <Spinner />
        </div>
      )}

      {/* Campaign Grid */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-20">
          <Sparkles size={40} className="mx-auto text-slate/30 mb-4" />
          <p className="text-slate text-sm">No campaigns found. Try adjusting your filters.</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((c) => {
            const ind = industryConfig(c.industry);
            const IndIcon = ind.icon;
            return (
              <Link
                key={c.id}
                to={`/campaigns/prebuilt/${c.slug}`}
                className="group block bg-white rounded-xl border border-sand hover:border-coral/40 hover:shadow-md transition-all p-5"
              >
                {/* Top row: industry badge + difficulty */}
                <div className="flex items-center justify-between mb-3">
                  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${ind.color}`}>
                    <IndIcon size={11} /> {ind.label}
                  </span>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${DIFFICULTY_BADGE[c.difficulty] || ""}`}>
                    {c.difficulty}
                  </span>
                </div>

                {/* Name + description */}
                <h3 className="font-display font-bold text-ink text-base leading-snug group-hover:text-coral transition-colors">
                  {c.name}
                </h3>
                <p className="text-slate text-xs mt-1.5 leading-relaxed line-clamp-2">
                  {c.description}
                </p>

                {/* Stats row */}
                <div className="flex items-center gap-4 mt-4 pt-3 border-t border-sand/60">
                  <span className="flex items-center gap-1 text-[11px] text-slate">
                    <Mail size={12} /> {c.step_count} emails
                  </span>
                  <span className="flex items-center gap-1 text-[11px] text-slate">
                    <Calendar size={12} /> {c.duration_days} days
                  </span>
                  {c.install_count > 0 && (
                    <span className="flex items-center gap-1 text-[11px] text-slate ml-auto">
                      <Download size={11} /> {c.install_count}
                    </span>
                  )}
                </div>

                {/* Tags */}
                {c.tags && c.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {c.tags.slice(0, 3).map(tag => (
                      <span key={tag} className="text-[10px] text-slate/70 bg-paper px-1.5 py-0.5 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
